/**
 * Traffic page controller — state shape + reducer.
 *
 * The /traffic page surfaces its mutable state (range / metric /
 * grouping) via the page-controller framework so the chat AI can drive it.
 *
 * The reducer is intentionally exhaustive on `action`: unknown actions
 * pass through with state unchanged so a forward-compatible chat tool
 * doesn't break older clients.
 */

import type { PageControllerMessage } from './types'

export type TrafficRangeKey = 1 | 7 | 30 | 'all'
export type TrafficMetric = 'sessions' | 'revenue'
export type TrafficGrouping = 'channel' | 'campaign' | 'creative' | 'landing_page'

export interface TrafficControllerState {
  range: TrafficRangeKey
  metric: TrafficMetric
  grouping: TrafficGrouping
}

export const TRAFFIC_DEFAULT_STATE: TrafficControllerState = {
  range: 7,
  metric: 'sessions',
  grouping: 'channel',
}

const RANGE_VALUES: ReadonlySet<TrafficRangeKey> = new Set([1, 7, 30, 'all'])
const METRIC_VALUES: ReadonlySet<TrafficMetric> = new Set(['sessions', 'revenue'])
const GROUPING_VALUES: ReadonlySet<TrafficGrouping> = new Set([
  'channel',
  'campaign',
  'creative',
  'landing_page',
])

/** Map the chat-tool range vocab (`'24h' | '7d' | '30d' | 'all'`) onto the page's `RangeKey`. */
export function rangeFromToolVocab(raw: unknown): TrafficRangeKey | null {
  if (raw === '24h' || raw === 1 || raw === '1') return 1
  if (raw === '7d' || raw === 7 || raw === '7') return 7
  if (raw === '30d' || raw === 30 || raw === '30') return 30
  if (raw === 'all') return 'all'
  return null
}

/** Inverse of {@link rangeFromToolVocab} — kept stable for tool responses. */
export function rangeToToolVocab(r: TrafficRangeKey): '24h' | '7d' | '30d' | 'all' {
  if (r === 1) return '24h'
  if (r === 7) return '7d'
  if (r === 30) return '30d'
  return 'all'
}

/**
 * Reduce a controller message into a new TrafficControllerState.
 *
 * Always returns a value; unknown actions and malformed payloads return
 * the previous state. Validation is strict so the chat tool can't push
 * garbage that survives a reload.
 */
export function trafficReducer(
  state: TrafficControllerState,
  msg: PageControllerMessage,
): TrafficControllerState {
  if (msg.page !== 'traffic') return state

  switch (msg.action) {
    case 'set_range': {
      const next = rangeFromToolVocab(msg.payload.range)
      if (next === null) return state
      return next === state.range ? state : { ...state, range: next }
    }
    case 'set_metric': {
      const raw = msg.payload.metric
      if (typeof raw !== 'string' || !METRIC_VALUES.has(raw as TrafficMetric)) return state
      const next = raw as TrafficMetric
      return next === state.metric ? state : { ...state, metric: next }
    }
    case 'set_grouping': {
      const raw = msg.payload.grouping
      if (typeof raw !== 'string' || !GROUPING_VALUES.has(raw as TrafficGrouping)) return state
      const next = raw as TrafficGrouping
      return next === state.grouping ? state : { ...state, grouping: next }
    }
    case 'reset': {
      return { ...TRAFFIC_DEFAULT_STATE }
    }
    default:
      return state
  }
}

export const TRAFFIC_RANGE_VALUES = RANGE_VALUES
export const TRAFFIC_METRIC_VALUES = METRIC_VALUES
export const TRAFFIC_GROUPING_VALUES = GROUPING_VALUES
